import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, 
  Search, 
  Send, 
  Smile, 
  Paperclip, 
  Phone, 
  Video, 
  MoreHorizontal, 
  X, 
  Image as ImageIcon,
  Twitter,
  Facebook,
  Instagram,
  Linkedin,
  Youtube,
  MessageCircle,
  Mail,
  AtSign,
  Hash,
  User,
  CheckCheck,
  Clock,
  Settings,
  AlertCircle,
  ArrowRight,
  Info,
  Menu,
  Bot
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import AIResponder from '../components/messaging/AIResponder';

// Empty platform data
const platformData = [
  { id: 'all', name: 'All', icon: MessageCircle, color: '#6366f1', count: 0 },
  { id: 'twitter', name: 'Twitter', icon: Twitter, color: '#1DA1F2', count: 0 },
  { id: 'facebook', name: 'Facebook', icon: Facebook, color: '#1877F2', count: 0 },
  { id: 'instagram', name: 'Instagram', icon: Instagram, color: '#E4405F', count: 0 },
  { id: 'linkedin', name: 'LinkedIn', icon: Linkedin, color: '#0A66C2', count: 0 },
  { id: 'youtube', name: 'YouTube', icon: Youtube, color: '#FF0000', count: 0 }
];

// Empty contacts array
const contacts: any[] = [];

// Empty messages array
const messages: any[] = [];

// Platform icons mapping
const PlatformIcon = ({ platform, className = "h-4 w-4" }: { platform: string, className?: string }) => {
  switch (platform) {
    case 'twitter':
      return <Twitter className={className} style={{ color: '#1DA1F2' }} />;
    case 'facebook':
      return <Facebook className={className} style={{ color: '#1877F2' }} />;
    case 'instagram':
      return <Instagram className={className} style={{ color: '#E4405F' }} />;
    case 'linkedin':
      return <Linkedin className={className} style={{ color: '#0A66C2' }} />;
    case 'youtube':
      return <Youtube className={className} style={{ color: '#FF0000' }} />;
    default:
      return <User className={className} />;
  }
};

const Messaging = () => {
  const [selectedContact, setSelectedContact] = useState(contacts[0]);
  const [messageInput, setMessageInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showNewMessageModal, setShowNewMessageModal] = useState(false);
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([]);
  const [activePlatformFilter, setActivePlatformFilter] = useState('all');
  const [sortOrder, setSortOrder] = useState<'recent' | 'unread'>('recent');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showContactDetails, setShowContactDetails] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messageInputRef = useRef<HTMLInputElement>(null);
  const [showAIResponder, setShowAIResponder] = useState(false);
  
  // Empty filtered contacts
  const filteredContacts: any[] = [];
  
  // Total unread count (always 0)
  const totalUnread = 0;

  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    if (!messageInput.trim()) return;
    
    // In a real app, this would send the message to the backend
    console.log('Sending message:', messageInput);
    setMessageInput('');
    setShowAIResponder(false);
    
    // Focus back on the input
    messageInputRef.current?.focus();
  };

  const handleAIResponse = (response: string) => {
    setMessageInput(response);
    setShowAIResponder(false);
    // Focus on the input after setting the AI response
    setTimeout(() => {
      messageInputRef.current?.focus();
    }, 0);
  };

  const formatTime = (time: string) => {
    // Simple time formatter
    return time;
  };

  return (
    <div className="flex flex-col overflow-hidden space-y-4">
      {/* Header with title and new message button */}
      <div className="flex justify-between items-center mb-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">Messaging</h1>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
            Connect with your audience across all platforms
          </p>
        </div>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setShowNewMessageModal(true)}
          className="platform-connect-btn flex items-center space-x-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm shadow-sm"
        >
          <Plus className="h-4 w-4" />
          <span className="hidden sm:inline">New Message</span>
        </motion.button>
      </div>

      {/* Main messaging interface */}
      <div className="messaging-container">
        <div className="messaging-content">
          {/* Sidebar - Contact List */}
          <div className={`conversations-panel ${isMobileMenuOpen ? 'open' : ''}`}>
            {/* Search and Filter */}
            <div className="search-container">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center">
                  <MessageCircle className="h-4 w-4 mr-1.5 text-indigo-600 dark:text-indigo-400" />
                  Messages
                  {totalUnread > 0 && (
                    <span className="ml-2 px-1.5 py-0.5 bg-indigo-600 text-white text-xs rounded-full">
                      {totalUnread}
                    </span>
                  )}
                </h2>
                <div className="flex items-center space-x-1">
                  <button 
                    onClick={() => setSortOrder(sortOrder === 'recent' ? 'unread' : 'recent')}
                    className="p-1.5 text-gray-500 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-gray-100 dark:hover:bg-dark-700 rounded-md transition-colors"
                    title={sortOrder === 'recent' ? 'Sorting by recent' : 'Sorting by unread'}
                  >
                    {sortOrder === 'unread' ? (
                      <MessageCircle className="h-4 w-4" />
                    ) : (
                      <Clock className="h-4 w-4" />
                    )}
                  </button>
                  <button 
                    onClick={() => setShowContactDetails(!showContactDetails)}
                    className={`p-1.5 text-gray-500 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-gray-100 dark:hover:bg-dark-700 rounded-md transition-colors ${
                      showContactDetails ? 'bg-gray-100 dark:bg-dark-700 text-indigo-600 dark:text-indigo-400' : ''
                    }`}
                    title="Contact details"
                  >
                    <Info className="h-4 w-4" />
                  </button>
                </div>
              </div>
              
              {/* Search Bar */}
              <div className="search-input-wrapper mb-3">
                <Search className="search-icon h-4 w-4" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search name or message..."
                  className="search-input"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              
              {/* Platform Tabs */}
              <div className="platform-filter" id="platform-filter">
                {platformData.map(platform => (
                  <button
                    key={platform.id}
                    onClick={() => setActivePlatformFilter(platform.id)}
                    data-platform={platform.id}
                    className={`platform-tab ${platform.id} ${
                      activePlatformFilter === platform.id
                        ? 'active'
                        : ''
                    }`}
                  >
                    <platform.icon className="platform-tab-icon" />
                    <span className="text-xs whitespace-nowrap">{platform.name}</span>
                  </button>
                ))}
              </div>
            </div>
            
            {/* Contacts List */}
            <div className="conversation-list no-scrollbar">
              <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                No conversations yet
              </div>
            </div>
            
            {/* Sidebar Footer */}
            <div className="p-3 border-t border-gray-200 dark:border-dark-700 bg-white dark:bg-dark-800">
              <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                <span>{filteredContacts.length} conversations</span>
                <button 
                  onClick={() => setShowNewMessageModal(true)}
                  className="flex items-center text-indigo-600 dark:text-indigo-400 hover:underline"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  New message
                </button>
              </div>
            </div>
          </div>
          
          {/* Mobile menu toggle */}
          <div className="md:hidden fixed bottom-4 left-4 z-10">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-3 bg-indigo-600 text-white rounded-full shadow-lg"
            >
              {isMobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
          
          {/* Message Content */}
          <div className="chat-area">
            <div className="empty-state">
              <div className="empty-state-icon">
                <Send className="h-8 w-8 text-indigo-600 dark:text-indigo-400" />
              </div>
              <h3 className="empty-state-title">
                Your Messages
              </h3>
              <p className="empty-state-description">
                Connect with your audience across all platforms in one unified inbox.
              </p>
              <button
                onClick={() => setShowNewMessageModal(true)}
                className="btn btn-primary"
              >
                <Plus className="h-4 w-4 mr-2" />
                New Message
              </button>
            </div>
                
            {/* Message Composer (hidden in empty state but added for completeness) */}
            <div className="message-composer hidden">
              <div className="relative flex-1">
                <input
                  type="text"
                  ref={messageInputRef}
                  value={messageInput}
                  onChange={(e) => setMessageInput(e.target.value)}
                  placeholder="Type a message..."
                  className="composer-input"
                />
                
                {/* AI Responder */}
                <AIResponder 
                  isVisible={showAIResponder}
                  onClose={() => setShowAIResponder(false)}
                  onSelectResponse={handleAIResponse}
                  messageContext=""
                />
                
                <div className="composer-actions">
                  <button 
                    className="composer-action"
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                  >
                    <Smile className="h-5 w-5" />
                  </button>
                  <button className="composer-action">
                    <Paperclip className="h-5 w-5" />
                  </button>
                </div>
              </div>
              
              {/* AI Assistant Button */}
              <button
                onClick={() => setShowAIResponder(!showAIResponder)}
                className="p-2 rounded-full bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-200 dark:hover:bg-indigo-900/50 transition-colors mr-2"
                title="Get AI suggestions"
              >
                <Bot className="h-5 w-5" />
              </button>
              
              <button 
                onClick={handleSendMessage}
                disabled={!messageInput.trim()}
                className={`send-button ${!messageInput.trim() ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <Send className="h-5 w-5" />
              </button>
            </div>
          </div>
          
          {/* Contact Details Panel */}
          <div className={`w-72 border-l border-gray-200 dark:border-dark-700 h-full ${showContactDetails ? 'block' : 'hidden lg:block'}`}>
            <div className="p-4 h-full flex items-center justify-center">
              <p className="text-gray-500 dark:text-gray-400 text-center">
                Select a conversation to view details
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* New Message Modal */}
      <AnimatePresence>
        {showNewMessageModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.5 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black z-40"
              onClick={() => setShowNewMessageModal(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="modal-overlay"
            >
              <div id="new-message-modal" className="modal-container"> 
                <div className="modal-header">
                  <h3 className="modal-title">New Message</h3>
                    <button
                      onClick={() => setShowNewMessageModal(false)}
                      className="modal-close"
                    >
                      <X className="h-5 w-5" />
                    </button>
                </div>
                  
                <div className="modal-body"> 
                  <div className="space-y-5">
                    <div className="form-group">
                      <label className="form-label">
                        Platform
                      </label>
                      <div className="platform-selector flex flex-wrap gap-2">
                        {platformData.filter(p => p.id !== 'all').map(platform => (
                          <button
                            key={platform.id}
                            className="flex items-center px-3 py-1.5 rounded-lg text-sm border border-gray-200 dark:border-dark-700 hover:border-indigo-500 dark:hover:border-indigo-500 transition-colors"
                          >
                            <platform.icon className="h-4 w-4 mr-1.5" style={{ color: platform.color }} />
                            <span>{platform.name}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    <div className="form-group">
                      <label className="form-label">
                        Account
                      </label>
                      <select className="account-selector form-input">
                        <option value="">Select Account</option>
                        <option value="account1">@youraccount</option>
                      </select>
                    </div>
                    
                    <div className="form-group">
                      <label className="form-label">
                        Recipient
                      </label>
                      <div className="relative">
                        <AtSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                          type="text"
                          className="recipient-input"
                          placeholder="Username or name"
                        />
                      </div>
                    </div>
                    
                    <div className="form-group">
                      <label className="form-label">
                        Message
                      </label>
                      <textarea
                        rows={4}
                        className="message-input form-textarea"
                        placeholder="Type your message..."
                      ></textarea>
                    </div>
                  </div>
                </div>
                
                <div className="modal-footer">
                  <button 
                    onClick={() => setShowNewMessageModal(false)}
                    className="btn btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    className="send-btn btn btn-primary"
                  >
                    <Send className="h-4 w-4 mr-2" />
                    Send Message
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Platform Connection Module */}
      <div className="mt-6"> 
        <div className="connection-module">
          <h3 className="connection-title">Connect Your Platforms</h3>
          <p className="connection-description">
            Link your social accounts to enable cross-platform messaging in one unified inbox.
          </p>
          
          <div className="connection-grid">
            {['twitter', 'facebook', 'instagram', 'linkedin', 'youtube'].map(platform => (
              <button
                key={platform} 
                data-platform={platform}
                className="connection-button"
              >
                <PlatformIcon platform={platform} className="h-6 w-6 mb-2" />
                <span className="text-xs font-medium capitalize">{platform}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Feature Coming Soon Banner */}
      <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/30 rounded-lg p-4 shadow-sm"> 
        <div className="flex items-center space-x-3">
          <AlertCircle className="h-5 w-5 text-amber-500 dark:text-amber-400 flex-shrink-0" />
          <div>
            <h3 className="text-sm font-medium text-amber-800 dark:text-amber-300">
              Enhanced Messaging Features Coming Soon
            </h3>
            <p className="text-sm text-amber-700 dark:text-amber-400 mt-1">
              We're working on adding message templates, scheduled messages, and integration with your social media DMs. Stay tuned!
            </p>
          </div>
          <button className="flex-shrink-0 text-amber-600 dark:text-amber-400 hover:text-amber-800 dark:hover:text-amber-300">
            <ArrowRight className="h-5 w-5" />
          </button>
        </div>
      </div>
      
      {/* AI Assistant Banner */}
      <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800/30 rounded-lg p-4 shadow-sm mt-4"> 
        <div className="flex items-center space-x-3">
          <Bot className="h-5 w-5 text-indigo-500 dark:text-indigo-400 flex-shrink-0" />
          <div>
            <h3 className="text-sm font-medium text-indigo-800 dark:text-indigo-300">
              AI Message Assistant
            </h3>
            <p className="text-sm text-indigo-700 dark:text-indigo-400 mt-1">
              Our AI assistant can help you craft the perfect response. Look for the AI button next to the send button when composing messages.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Messaging;